"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { useUser } from "@clerk/nextjs";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Lock,
  NotebookPen,
  Save,
  Loader2,
} from "lucide-react";
import { LocalNoteData } from "@/types/note";
import BlockEditor from "./BlockEditor";
import Section from "@/components/article-shell/Section";
import { useArticleShellSlots } from "@/components/article-shell/ArticleShellContext";
import { MobileBottomToolRegistration } from "@/components/mobile-bottom-tools";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { PASS_FEATURES } from "@/types/pass";
import { uploadBlockEditorImages } from "@/lib/block-editor-image-upload";
import { STUDY_PAGE_COMPLETED } from "@/lib/study-task-link";
import {
  getNoteStorageKey,
  isNoteDraftBeforeReset,
  parseLocalNoteData,
  shouldUseFirestoreNoteContent,
} from "@/lib/note-local-draft";

// 필기(Note) 노출 경로는 두 가지뿐이다 (JCG-2224, 플로팅 제거):
//   - 클래식 레이아웃(<1024px): 하단 도구 Dock(MobileBottomToolRegistration) — 잠금 상태도 sheet 안 안내로 노출
//   - 데스크탑 셸(≥1024px): 우측 사이드바 Section portal(noteSlot)
// 드래그 플로팅 아이콘·커스텀 바텀시트·데스크탑 사이드 패널은 모두 제거됐다.

const SHELL_SECTION_KEY = "ui2-sec-note";

interface NoteProps {
  pagePath: string;
  pageTitle: string;
  subject: string;
}

export default function Note({ pagePath, pageTitle, subject }: NoteProps) {
  const { user, isSignedIn } = useUser();
  const userId = user?.id;
  const [content, setContent] = useState("");
  const [hasMounted, setHasMounted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const contentRef = useRef("");
  const imageFileMapRef = useRef<Map<string, File>>(new Map());
  const editVersionRef = useRef(0);
  const lastSavedVersionRef = useRef(0);
  const saveInFlightRef = useRef<Promise<boolean> | null>(null);
  const noteLoadPromiseRef = useRef<Promise<void> | null>(null);
  const noteLoadSeqRef = useRef(0);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 3컬럼 셸(UI 2.0) 우측 사이드바 슬롯. Provider 없으면 inShell:false → 클래식 Dock 노출.
  const { inShell, noteSlot } = useArticleShellSlots();
  const { isLoaded: passLoaded, hasAccess } = useEffectivePass();
  const hasNoteAccess = passLoaded && hasAccess(PASS_FEATURES.noteFeature);
  const [shellOpen, setShellOpen] = useState(true);

  const setContentState = useCallback((nextContent: string) => {
    contentRef.current = nextContent;
    setContent(nextContent);
  }, []);

  const handleImageFilesChange = useCallback((nextMap: Map<string, File>) => {
    imageFileMapRef.current = nextMap;
  }, []);

  // 셸 Section 접힘 상태 복원 (lazy init 금지 — hydration mismatch 회피, 마운트 후 effect에서 복원)
  useEffect(() => {
    const saved = localStorage.getItem(SHELL_SECTION_KEY);
    if (saved !== null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShellOpen(saved === "true");
    }
  }, []);

  const toggleShellOpen = useCallback(() => {
    setShellOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SHELL_SECTION_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // 마운트 게이트 (hydration mismatch 회피 — 클라이언트에서만 Dock/portal 렌더)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasMounted(true);
  }, []);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // localStorage에서 노트 내용 불러오기 — 외부 시스템 동기화 + JCG-2034 마이그레이션 예정
  useEffect(() => {
    if (!userId || !hasNoteAccess) return;

    const requestSeq = ++noteLoadSeqRef.current;
    const editVersionAtRequest = editVersionRef.current;

    // 노트 내용 불러오기 (localStorage 우선)
    const noteKey = getNoteStorageKey(userId, pagePath);
    const savedNote = localStorage.getItem(noteKey);
    const localNote = parseLocalNoteData(savedNote);
    const localContent = localNote?.content || "";
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setContentState(localContent);
    setLastSaved(null);
    setHasUnsavedChanges(false);
    handleImageFilesChange(new Map());

    // Firestore에서 마지막 저장 시간 가져오기 (백그라운드)
    const fetchFirestoreNote = async () => {
      try {
        const response = await fetch(`/api/notes/get?pagePath=${encodeURIComponent(pagePath)}`);
        const data = await response.json();
        if (requestSeq !== noteLoadSeqRef.current) return;

        const localWasReset = isNoteDraftBeforeReset(localNote, data.resetAt);
        const canApplyRemoteContent =
          editVersionRef.current === editVersionAtRequest &&
          contentRef.current === localContent;
        if (localWasReset && canApplyRemoteContent) {
          setContentState("");
          setLastSaved(null);
          localStorage.removeItem(noteKey);
        }

        if (data.note?.lastSaved) {
          setLastSaved(data.note.lastSaved);
          const firestoreContent = typeof data.note.content === "string" ? data.note.content : "";
          const shouldUseFirestore = shouldUseFirestoreNoteContent({
            localContent: localWasReset ? null : localNote?.content,
            localSavedAt: localWasReset ? null : localNote?.lastLocalSaved,
            firestoreContent,
            firestoreSavedAt: data.note.lastSaved,
          });

          if (shouldUseFirestore && canApplyRemoteContent) {
            setContentState(firestoreContent);
            const noteData: LocalNoteData = {
              content: firestoreContent,
              lastLocalSaved: data.note.lastSaved,
            };
            localStorage.setItem(noteKey, JSON.stringify(noteData));
          } else if (
            canApplyRemoteContent &&
            !localWasReset &&
            localContent !== firestoreContent &&
            Date.parse(localNote?.lastLocalSaved ?? "") > Date.parse(data.note.lastSaved)
          ) {
            // 이전 방문에서 남긴 최신 로컬 초안도 출석 시 서버에 반영한다.
            editVersionRef.current += 1;
            setHasUnsavedChanges(true);
          }
        } else if (canApplyRemoteContent && !localWasReset && localContent) {
          editVersionRef.current += 1;
          setHasUnsavedChanges(true);
        }
      } catch (error) {
        console.error("Firestore 노트 조회 오류:", error);
      }
    };

    noteLoadPromiseRef.current = fetchFirestoreNote();

    return () => {
      noteLoadSeqRef.current += 1;
    };
  }, [userId, pagePath, hasNoteAccess, setContentState, handleImageFilesChange]);

  // 컨텐츠 변경 시 localStorage에 자동 저장 (debounce)
  const saveToLocalStorage = useCallback((newContent: string) => {
    if (!userId || !hasNoteAccess) return;

    const noteKey = getNoteStorageKey(userId, pagePath);
    const noteData: LocalNoteData = {
      content: newContent,
      lastLocalSaved: new Date().toISOString(),
    };
    localStorage.setItem(noteKey, JSON.stringify(noteData));
  }, [userId, pagePath, hasNoteAccess]);

  // BlockEditor 콘텐츠 변경 핸들러
  const handleContentChange = useCallback((newContent: string) => {
    editVersionRef.current += 1;
    setContentState(newContent);
    setHasUnsavedChanges(true);

    // 기존 타이머 취소
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // 500ms 후 localStorage에 저장
    saveTimeoutRef.current = setTimeout(() => {
      saveToLocalStorage(newContent);
    }, 500);
  }, [saveToLocalStorage, setContentState]);

  // Firestore에 저장
  const handleSaveToFirestore = useCallback((): Promise<boolean> => {
    if (!userId || !hasNoteAccess) return Promise.resolve(false);
    if (saveInFlightRef.current) return saveInFlightRef.current;

    setIsSaving(true);
    const save = async (): Promise<boolean> => {
      const editVersionAtSaveStart = editVersionRef.current;
      const contentAtSaveStart = contentRef.current;
      const imageFileMapAtSaveStart = new Map(imageFileMapRef.current);
      try {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
        }
        // 출석 직후 500ms 임시 저장 타이머가 취소돼도 작성 중인 초안은 남긴다.
        try {
          saveToLocalStorage(contentAtSaveStart);
        } catch {
          // 브라우저 저장소 실패가 서버 저장을 막지 않도록 한다.
        }

        const processedContent = await uploadBlockEditorImages({
          content: contentAtSaveStart,
          imageFileMap: imageFileMapAtSaveStart,
          uploadEndpoint: "/api/upload-note-image",
        });
        const hasEditedBeforeUploadCompleted =
          editVersionRef.current !== editVersionAtSaveStart;

        if (processedContent !== contentAtSaveStart && !hasEditedBeforeUploadCompleted) {
          setContentState(processedContent);
          handleImageFilesChange(new Map());
          saveToLocalStorage(processedContent);
        }

        const response = await fetch("/api/notes/save", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            content: processedContent,
            pagePath,
            pageTitle,
            subject,
          }),
        });

        const data = await response.json();
        if (data.success) {
          lastSavedVersionRef.current = editVersionAtSaveStart;
          setLastSaved(data.lastSaved);
          const hasEditedBeforeSaveCompleted =
            editVersionRef.current !== editVersionAtSaveStart;
          if (!hasEditedBeforeSaveCompleted) {
            setHasUnsavedChanges(false);
            setContentState(processedContent);
            handleImageFilesChange(new Map());
            saveToLocalStorage(processedContent);
          }
          return true;
        }
        console.error("노트 저장 실패:", data.error);
        alert("노트 저장에 실패했습니다.");
        return false;
      } catch (error) {
        console.error("노트 저장 오류:", error);
        alert("노트 저장 중 오류가 발생했습니다.");
        return false;
      }
    };
    const promise = save().finally(() => {
      saveInFlightRef.current = null;
      setIsSaving(false);
    });
    saveInFlightRef.current = promise;
    return promise;
  }, [
    userId,
    hasNoteAccess,
    pagePath,
    pageTitle,
    subject,
    saveToLocalStorage,
    setContentState,
    handleImageFilesChange,
  ]);

  // 출석 확정 시 이 페이지에서 작성한 필기만 서버에 저장한다. 저장 중에 추가로
  // 편집했다면 첫 저장이 끝난 뒤 최신 내용을 한 번 더 저장한다.
  useEffect(() => {
    const saveOnAttendance = (event: Event) => {
      const detail = (event as CustomEvent<{ slug?: string; source?: string }>).detail;
      if (detail?.source !== "attendance" || `/${detail.slug}` !== pagePath) return;
      void (async () => {
        if (noteLoadPromiseRef.current) await noteLoadPromiseRef.current;
        const versionAtAttendance = editVersionRef.current;
        if (versionAtAttendance <= lastSavedVersionRef.current) return;
        if (saveInFlightRef.current) await saveInFlightRef.current;
        if (versionAtAttendance > lastSavedVersionRef.current) {
          await handleSaveToFirestore();
        }
      })();
    };
    window.addEventListener(STUDY_PAGE_COMPLETED, saveOnAttendance);
    return () => window.removeEventListener(STUDY_PAGE_COMPLETED, saveOnAttendance);
  }, [pagePath, handleSaveToFirestore]);

  // 마지막 저장 시간 포맷팅
  const formatLastSaved = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "방금 전";
    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString("ko-KR", {
      month: "short",
      day: "numeric"
    });
  };

  // 저장 버튼 + 마지막 저장/미저장 문구 (셸 Section / Dock sheet 공용; 색상·wrapper만 variant로 차별화)
  const renderSaveFooter = (variant: {
    wrapperClassName: string;
    buttonClassName: string;
    unsavedClassName: string;
    lastSavedClassName: string;
  }) => (
    <div className={variant.wrapperClassName}>
      <Button
        onClick={handleSaveToFirestore}
        disabled={isSaving}
        size="sm"
        className={variant.buttonClassName}
      >
        {isSaving ? (
          <>
            <Loader2 className="h-3 w-3 mr-2 animate-spin" />
            저장 중...
          </>
        ) : (
          <>
            <Save className="h-3 w-3 mr-2" />
            저장
          </>
        )}
      </Button>

      {hasUnsavedChanges ? (
        <p className={variant.unsavedClassName}>
          변경사항이 저장되지 않았습니다
        </p>
      ) : lastSaved ? (
        <p className={variant.lastSavedClassName}>
          마지막 저장: {formatLastSaved(lastSaved)}
        </p>
      ) : null}
    </div>
  );

  const lockedNoteContent = (
    <div className="flex h-full min-h-[9rem] flex-col items-center justify-center rounded-md border border-dashed border-amber-300 bg-amber-50/70 px-4 py-6 text-center dark:border-amber-800 dark:bg-amber-950/25">
      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300">
        <Lock className="h-5 w-5" aria-hidden />
      </div>
      <p className="text-sm font-semibold text-foreground">
        필기는 Starter 이상에서 사용할 수 있어요.
      </p>
      <p className="mt-1 max-w-[18rem] text-xs leading-5 text-muted-foreground">
        {isSignedIn
          ? "이용권을 업그레이드하면 이 페이지에 메모를 작성하고 저장할 수 있습니다."
          : "로그인 후 Starter 이상 이용권으로 메모를 작성하고 저장할 수 있습니다."}
      </p>
      <Link
        href="/membership"
        className="mt-4 inline-flex h-9 items-center justify-center rounded-md bg-amber-500 px-3 text-sm font-semibold text-white transition-colors hover:bg-amber-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background dark:bg-amber-400 dark:text-amber-950 dark:hover:bg-amber-300"
      >
        이용권 보기
      </Link>
    </div>
  );

  // 마운트 전에는 렌더링하지 않음
  if (!hasMounted) {
    return null;
  }

  // 3컬럼 셸 안: 우측 사이드바 슬롯에 접이식 Section 으로 필기 portal.
  // fixed/maxh/z/드래그 없이 흐름 레이아웃만 — 가시성은 Section 의 open 이 담당.
  if (inShell && noteSlot) {
    if (!hasNoteAccess) return null;
    return createPortal(
      <Section
        icon={NotebookPen}
        label="필기"
        amber
        fill={shellOpen}
        open={shellOpen}
        onToggle={toggleShellOpen}
      >
        {/* fill 컨텍스트: 편집기가 남은 높이를 채우고 내부 스크롤. 부모가 flex-1 min-h-0 로 높이 확정. */}
        <div className="flex min-h-0 flex-1 flex-col">
          <BlockEditor
            fill
            value={content}
            onChange={handleContentChange}
            onImageFilesChange={handleImageFilesChange}
            placeholder="이 페이지에 대한 메모를 작성하세요..."
            className={cn(
              "bg-muted",
              "border-border",
              hasUnsavedChanges && "border-amber-300 dark:border-amber-700",
            )}
          />
        </div>
        {/* 저장 버튼 + 마지막 저장 문구: 편집기 아래 하단 고정 */}
        {renderSaveFooter({
          wrapperClassName: "mt-3 shrink-0 space-y-2",
          buttonClassName: "w-full bg-amber-500 hover:bg-amber-600 text-white",
          unsavedClassName: "text-center text-xs text-amber-600 dark:text-amber-400",
          lastSavedClassName: "text-center text-xs text-muted-foreground",
        })}
      </Section>,
      noteSlot,
    );
  }

  // 셸 안(데스크탑 글 페이지)에서는 슬롯이 아직 없거나 비대상이어도 플로팅/바텀시트를
  // 그리지 않는다 — fixed 패널이 셸 위에 겹치는 것을 방지(슬롯 마운트 직전 깜빡임 포함).
  if (inShell) {
    return null;
  }

  // 클래식 레이아웃(<1024px): 하단 도구 Dock 으로만 노출 (JCG-2224, 플로팅 제거).
  // 잠금 상태(hasNoteAccess=false)도 Dock 버튼은 노출하되 sheet 안에서 업그레이드 안내를 보여준다.
  return (
    <MobileBottomToolRegistration
      tool={{
        id: "note",
        kind: "panel",
        label: "필기",
        icon: NotebookPen,
        order: 10,
        ariaLabel: "필기 패널 열기",
        status: hasNoteAccess && hasUnsavedChanges ? "변경" : undefined,
        sheet: {
          title: "필기",
          description: hasNoteAccess
            ? "이 페이지에 대한 메모를 작성하고 저장할 수 있습니다."
            : "Starter 이상 이용권에서 사용할 수 있습니다.",
          resizable: true,
          defaultHeightRatio: 0.3,
          minHeightRatio: 0.3,
          maxHeightRatio: 0.85,
          content: (
            <div className="flex h-full min-h-0 flex-col">
              {hasNoteAccess ? (
                <>
                  <BlockEditor
                    fill
                    value={content}
                    onChange={handleContentChange}
                    onImageFilesChange={handleImageFilesChange}
                    placeholder="이 페이지에 대한 메모를 작성하세요..."
                    className={cn(
                      "bg-muted",
                      "border-border",
                      hasUnsavedChanges && "border-amber-300 dark:border-amber-700",
                    )}
                  />
                  {renderSaveFooter({
                    wrapperClassName: "mt-3 shrink-0 space-y-2 border-t border-border pt-3",
                    buttonClassName:
                      "w-full bg-amber-500 text-white hover:bg-amber-600 dark:bg-amber-400 dark:text-amber-950 dark:hover:bg-amber-300",
                    unsavedClassName:
                      "text-center text-xs text-amber-600 dark:text-amber-400",
                    lastSavedClassName: "text-center text-xs text-muted-foreground",
                  })}
                </>
              ) : (
                lockedNoteContent
              )}
            </div>
          ),
        },
      }}
    />
  );
}
